/**
 * Offline authentication (D5, D14, D20; PLAN §11): what a node needs to let a
 * working operator keep working while the internet is gone, and the
 * break-glass ways in when nobody is signed in yet.
 *
 *   1. THE DIRECTORY, synced cloud → node over the site link
 *      (`directory.snapshot` / `directory.delta`, `siteLink.ts`): the site's
 *      tenant users, their groups (permissions + scope), memberships, the
 *      Argon2id hashes of their offline PINs and of the site's emergency
 *      password, and the Cognito JWKS. Revocation is EVENTUAL: a change in
 *      the cloud reaches a connected node as a delta within seconds, and a
 *      node that missed one heals on its next snapshot (on every reconnect,
 *      and every `DIRECTORY_RESYNC_SECONDS` while connected).
 *   2. COGNITO TOKENS OFFLINE: a node verifies a Cognito ID/access token
 *      against the synced JWKS until the token expires; the grants come from
 *      the synced directory, never from the token.
 *   3. LOCAL SESSIONS: a node exchanges a valid Cognito token for a local
 *      session token — an EdDSA JWT signed with the SITE KEY that main and
 *      backup share, 12 h, sliding, bound to user + tenant + site — so both
 *      nodes accept it and an internet outage mid-show never signs anyone
 *      out. Every request re-checks the user against the synced directory.
 *   4. BREAK-GLASS, only while the node's cloud link is DOWN
 *      (`CLOUD_LINK_DOWN_AFTER_MS`, the node's `offline/cloudLink.ts`): an offline PIN (users holding
 *      `local.login`) or the site's emergency account. Rate-limited, locked
 *      out after repeated failures, and every attempt audited on the node and
 *      reported to the cloud (`directory.audit`) once the link is back.
 *
 * The node-local routes (`POST /local/login`, `POST /local/session`,
 * `GET /local/auth`) are not in the route table: like `/health` and
 * `/dev/token`, they answer before a caller has a node credential, and only a
 * node serves them. Their shapes are here so the node, the CLI and the SPA
 * share them.
 */
import { openEnum } from './openEnum.js';
import { z } from 'zod';
import { emailWireSchema, grantScopeWireSchema } from './tenancy.js';
import { boundedIdWireSchema, cloudLinkStateReadWireSchema, entityIdWireSchema, isoTimestampWireSchema, tenantIdWireSchema, userIdWireSchema } from './primitives.js';

// ─── timings (each one cited where it is used) ───

/** A local session lives this long after its last use (D20: "12 h, sliding"). */
export const LOCAL_SESSION_TTL_SECONDS = 12 * 60 * 60;

/**
 * A request made with a local session older than this is answered with a
 * renewed one (`LOCAL_SESSION_HEADER`), so "sliding" costs one signature per
 * five minutes of activity rather than one per request.
 */
export const LOCAL_SESSION_RENEW_AFTER_SECONDS = 5 * 60;

/** The response header a node puts a new or renewed local session token in. */
export const LOCAL_SESSION_HEADER = 'x-acp-session';

/** A connected node asks for a fresh snapshot this often, healing any delta it missed. */
export const DIRECTORY_RESYNC_SECONDS = 10 * 60;

/** Break-glass: failures per account before a lockout, and how long it lasts. */
export const LOCAL_LOGIN_MAX_FAILURES = 5;
export const LOCAL_LOGIN_LOCKOUT_SECONDS = 15 * 60;
/** Break-glass: attempts one source address may make per minute, whatever the account. */
export const LOCAL_LOGIN_ATTEMPTS_PER_MINUTE = 20;

// ─── secrets ───

/** An offline PIN or password: at least six characters (the hash leaves the cloud: longer is stronger). */
export const offlinePinWireSchema = z.string().min(6, 'an offline PIN has at least 6 characters').max(128);

/** The account name the emergency account signs in with. */
export const EMERGENCY_ACCOUNT = 'emergency' as const;

// ─── the directory on the wire (site link; payloads strip unknown fields, siteLink.ts) ───

/** An Argon2id PHC string (`@norna/cloud-link/argon2.js`); its parameters are re-checked where it is verified. */
const secretHashWireSchema = z.string().regex(/^\$argon2id\$/).max(256);

/**
 * A group as a node holds it. Permission ids are plain strings on the wire: a
 * newer cloud may send an id this node does not know, which it drops on read
 * rather than refusing the whole snapshot (PLAN §6.8).
 */
export const directoryGroupWireSchema = z.object({
  groupId: entityIdWireSchema,
  name: z.string().max(200),
  permissions: z.array(z.string().max(64)).max(64),
  scope: grantScopeWireSchema,
});
export type DirectoryGroupWire = z.infer<typeof directoryGroupWireSchema>;

/** A user as a node holds it, with their groups and the hash of their offline PIN (null: none set). */
export const directoryUserWireSchema = z.object({
  userId: userIdWireSchema,
  email: emailWireSchema,
  displayName: z.string().max(200),
  /**
   * `disabled`: known, and refused. Today every user the cloud has is `active`; a deleted user is absent. Open: a
   * newer cloud may send a status this node does not know, which it refuses as it refuses `disabled` (only `active`
   * signs in: `offline/authenticate.ts`, `offline/localLogin.ts`).
   */
  status: openEnum(['active', 'disabled']),
  groupIds: z.array(entityIdWireSchema).max(200),
  offlinePin: z.object({ hash: secretHashWireSchema, updatedAt: isoTimestampWireSchema }).nullable(),
});
export type DirectoryUserWire = z.infer<typeof directoryUserWireSchema>;

/** The site's emergency account: its password hash and when it was last rotated (a rotation ends its sessions). */
export const emergencyAccountWireSchema = z.object({ passwordHash: secretHashWireSchema, rotatedAt: isoTimestampWireSchema });
export type EmergencyAccountWire = z.infer<typeof emergencyAccountWireSchema>;

/** A JWK as JWKS carries it: the fields RSA and EC signing keys use, the rest kept. */
const jwkWireSchema = z
  .object({ kty: z.string(), kid: z.string().max(256).optional(), alg: z.string().optional(), use: z.string().optional() })
  .loose();

/** The Cognito pool's key set, as the cloud fetched it. */
export const directoryJwksWireSchema = z.object({
  issuer: z.url(),
  keys: z.array(jwkWireSchema).max(20),
  fetchedAt: isoTimestampWireSchema,
});
export type DirectoryJwksWire = z.infer<typeof directoryJwksWireSchema>;

// ─── audit ───

/**
 * `offline-pin` / `emergency`: a break-glass sign-in (D20). `forced-promotion`: an operator forced a standby on air
 * (`forcePromoteProduction`, wave 7b-tail C) — node-local and audited like a break-glass, through the same reported,
 * idempotent queue; its `account` is the operator's user id and `productionId` / `detail` say what and why.
 */
/**
 * Open (D37 §4.3): a newer node reports a kind or an outcome an older cloud does not know, and the cloud stores and
 * lists the event rather than refusing the batch (which the node would re-send forever).
 */
const offlineAuditKindWireSchema = openEnum(['offline-pin', 'emergency', 'forced-promotion']);
const offlineAuditOutcomeWireSchema = openEnum(['success', 'failure', 'locked', 'refused-online', 'rate-limited']);

/** One break-glass attempt, as the node recorded it and reports it (`directory.audit`). */
export const offlineAuditEventWireSchema = z.object({
  /** A ULID the node minted: the cloud stores an id once, so a re-sent batch is idempotent. */
  id: z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/),
  at: isoTimestampWireSchema,
  kind: offlineAuditKindWireSchema,
  outcome: offlineAuditOutcomeWireSchema,
  /** What was typed as the account: an email, or `emergency`. Never the secret. */
  account: z.string().max(254),
  /** The user it resolved to, or null (unknown account, emergency). */
  userId: z.string().max(128).nullable(),
  sourceIp: z.string().max(64).nullable(),
  nodeId: boundedIdWireSchema,
  /** The production it concerns (a forced promotion); absent for a sign-in and from older nodes. */
  productionId: z.string().max(64).nullable().optional(),
  /** What happened, for people (a forced promotion's term, evidence and reason); absent for a sign-in. */
  detail: z.string().max(1000).nullable().optional(),
});
export type OfflineAuditEventWire = z.infer<typeof offlineAuditEventWireSchema>;

/** A tenant audit entry as the cloud stores and lists it. */
export const auditEntryWireSchema = z
  .object({
    auditId: z.string(),
    tenantId: tenantIdWireSchema,
    siteId: z.string(),
    at: isoTimestampWireSchema,
    receivedAt: isoTimestampWireSchema,
    kind: offlineAuditKindWireSchema,
    outcome: offlineAuditOutcomeWireSchema,
    account: z.string(),
    userId: z.string().nullable(),
    sourceIp: z.string().nullable(),
    nodeId: boundedIdWireSchema,
    /** The production a forced promotion concerns (absent for a sign-in). */
    productionId: z.string().nullable().optional(),
    detail: z.string().nullable().optional(),
  })
  .strict();
export type AuditEntryWire = z.infer<typeof auditEntryWireSchema>;

// ─── cloud REST ───

export const setOfflinePinRequestWireSchema = z.object({ pin: offlinePinWireSchema }).strict();
export const offlinePinResponseWireSchema = z.object({ set: z.boolean(), updatedAt: isoTimestampWireSchema.nullable() }).strict();

export const emergencyAccountRequestWireSchema = z
  .object({
    /** True: replace an existing account's password (its sessions end on every node at the next sync). False: create it — an existing account is `conflict`. */
    rotate: z.boolean(),
  })
  .strict();

/** The emergency account's password is in this response ONCE: the cloud keeps only its hash. */
export const emergencyAccountResponseWireSchema = z
  .object({ siteId: entityIdWireSchema, account: z.literal(EMERGENCY_ACCOUNT), password: z.string(), rotatedAt: isoTimestampWireSchema, rotated: z.boolean() })
  .strict();

/** `GET /sites/{siteId}/emergency-account`: whether the site has one, and since when — never its password. */
export const emergencyAccountStatusWireSchema = z.object({ siteId: entityIdWireSchema, exists: z.boolean(), rotatedAt: isoTimestampWireSchema.nullable() }).strict();
export type EmergencyAccountStatusWire = z.infer<typeof emergencyAccountStatusWireSchema>;

export const listAuditQueryWireSchema = z
  .object({
    limit: z.coerce.number().int().min(1).max(200).optional().describe('At most this many entries (default 50).'),
    before: z.string().max(200).optional().describe('The `next` cursor of a previous page.'),
  })
  .strict();
export const listAuditResponseWireSchema = z.object({ entries: z.array(auditEntryWireSchema), next: z.string().nullable() }).strict();

// ─── node-local REST ───

/** `POST /local/login`: break-glass. `account` is the user's email, or `emergency`. */
export const localLoginRequestWireSchema = z
  .object({ account: z.union([emailWireSchema, z.literal(EMERGENCY_ACCOUNT)]), secret: z.string().min(1).max(128) })
  .strict();
export type LocalLoginRequestWire = z.infer<typeof localLoginRequestWireSchema>;

/** Open: the SPA and the CLI read it from a node of another release. */
export const localSessionKindWireSchema = openEnum(['cognito', 'offline-pin', 'emergency', 'dev']);
export type LocalSessionKindWire = z.infer<typeof localSessionKindWireSchema>;

/** A new local session (`POST /local/login`, `POST /local/session`). */
export const localSessionResponseWireSchema = z
  .object({
    token: z.string(),
    expiresAt: isoTimestampWireSchema,
    kind: localSessionKindWireSchema,
    userId: z.string(),
    email: z.string().nullable(),
    tenantId: z.string(),
    siteId: z.string(),
  })
  .strict();
export type LocalSessionResponseWire = z.infer<typeof localSessionResponseWireSchema>;

/** `GET /local/auth` (unauthenticated): what sign-in this node offers right now. */
export const localAuthStatusWireSchema = z
  .object({
    /** Read as the node's link state; a value from a newer node reads as `disconnected` (as far as a reader can tell). */
    cloudLink: cloudLinkStateReadWireSchema,
    /** True exactly while the link is down (`CLOUD_LINK_DOWN_AFTER_MS`) — the only time `/local/login` is accepted. */
    offlineLogin: z.boolean(),
    /** Whether this node holds a site key, so it can issue local sessions at all. */
    localSessions: z.boolean(),
    /** When the node last heard from the cloud, or null. */
    lastCloudContact: isoTimestampWireSchema.nullable(),
    siteId: z.string(),
  })
  .strict();
export type LocalAuthStatusWire = z.infer<typeof localAuthStatusWireSchema>;
